import { useState, useCallback } from "react";
import { apiClient } from "../../../utils/apiClient";
import { useSnackbar } from "../../../components/SnackbarProvider";
import type { EmailActionType } from "./actionDefinitions";
import { getAction } from "./actionDefinitions";
import type {
  EmailReference,
  AddToTestSetParams,
  CreateWorkflowEntityParams,
  SingleEmailActionResponse,
  BatchEmailActionResponse,
  EmailSelectionState,
} from "./types";

interface UseBatchEmailActionsProps {
  selection: EmailSelectionState;
}

interface UseBatchEmailActionsReturn {
  /** Execute an action on selected emails */
  executeAction: (
    action: EmailActionType,
    params?: AddToTestSetParams | CreateWorkflowEntityParams
  ) => Promise<SingleEmailActionResponse | BatchEmailActionResponse | null>;
  /** Whether an action is currently executing */
  isExecuting: boolean;
  /** Open test set dialog for addToTestSet action */
  testSetDialogOpen: boolean;
  /** Set test set dialog open state */
  setTestSetDialogOpen: (open: boolean) => void;
  /** Open workflow entity dialog for createWorkflowEntity action */
  workflowEntityDialogOpen: boolean;
  /** Set workflow entity dialog open state */
  setWorkflowEntityDialogOpen: (open: boolean) => void;
  /** Handle action button click - opens dialog if needed or executes directly */
  handleActionClick: (action: EmailActionType) => void;
  /** Handle test set dialog confirmation */
  handleTestSetConfirm: (params: AddToTestSetParams) => Promise<void>;
  /** Handle workflow entity dialog confirmation */
  handleWorkflowEntityConfirm: (params: CreateWorkflowEntityParams) => Promise<void>;
}

/**
 * Hook for handling batch email actions
 * Encapsulates all the logic for executing actions on selected emails
 */
export function useBatchEmailActions({
  selection,
}: UseBatchEmailActionsProps): UseBatchEmailActionsReturn {
  const [isExecuting, setIsExecuting] = useState(false);
  const [testSetDialogOpen, setTestSetDialogOpen] = useState(false);
  const [workflowEntityDialogOpen, setWorkflowEntityDialogOpen] = useState(false);
  const { showSnackbar } = useSnackbar();

  /**
   * Execute an action on selected emails via the API
   */
  const executeAction = useCallback(
    async (
      action: EmailActionType,
      params?: AddToTestSetParams | CreateWorkflowEntityParams
    ): Promise<SingleEmailActionResponse | BatchEmailActionResponse | null> => {
      const emails: EmailReference[] = Array.from(selection.selectedEmails.values());

      if (emails.length === 0) {
        showSnackbar("No emails selected", 3000, "error");
        return null;
      }

      setIsExecuting(true);

      try {
        // Use single endpoint for one email, batch endpoint for multiple
        if (emails.length === 1) {
          const response = await apiClient.post<SingleEmailActionResponse>(
            "/admin/email_action",
            {
              action,
              email: emails[0],
              params,
            },
            { withCredentials: true }
          );

          if (response.success) {
            const message = getSuccessMessage(action, 1, response);
            showSnackbar(message, 3000, "success");
          } else {
            showSnackbar(response.error || "Action failed", 4000, "error");
          }

          return response;
        } else {
          // Batch action
          const actionDef = getAction(action);
          if (!actionDef.supportsBatch) {
            showSnackbar(`${actionDef.label} cannot be batched`, 3000, "error");
            return null;
          }

          const response = await apiClient.post<BatchEmailActionResponse>(
            "/admin/email_action/batch",
            {
              action,
              emails,
              params,
            },
            { withCredentials: true }
          );

          const message = getSuccessMessage(action, emails.length, response);
          const severity = response.failureCount > 0 ? "error" : "success";
          showSnackbar(message, 4000, severity);

          return response;
        }
      } catch (error: any) {
        console.error("Email action error:", error);
        const errorMessage =
          error?.response?.data?.error || error.message || "Action failed";
        showSnackbar(errorMessage, 4000, "error");
        return null;
      } finally {
        setIsExecuting(false);
      }
    },
    [selection.selectedEmails, showSnackbar]
  );

  /**
   * Handle action button click - opens dialog if needed or executes directly
   */
  const handleActionClick = useCallback(
    (action: EmailActionType) => {
      const actionDef = getAction(action);

      if (actionDef.requiresDialog) {
        if (action === "createWorkflowEntity") {
          setWorkflowEntityDialogOpen(true);
        } else {
          setTestSetDialogOpen(true);
        }
        return;
      }

      // Execute directly and deselect on success
      executeAction(action).then((result) => {
        if (result) {
          const hasSuccess =
            "successCount" in result ? result.successCount > 0 : result.success;
          if (hasSuccess) {
            selection.deselectAll();
          }
        }
      });
    },
    [executeAction, selection]
  );

  /**
   * Handle test set dialog confirmation
   */
  const handleTestSetConfirm = useCallback(
    async (params: AddToTestSetParams) => {
      const result = await executeAction("addToTestSet", params);
      setTestSetDialogOpen(false);

      if (result) {
        const hasSuccess =
          "successCount" in result ? result.successCount > 0 : result.success;
        if (hasSuccess) {
          selection.deselectAll();
        }
      }
    },
    [executeAction, selection]
  );

  /**
   * Handle workflow entity dialog confirmation
   */
  const handleWorkflowEntityConfirm = useCallback(
    async (params: CreateWorkflowEntityParams) => {
      const result = await executeAction("createWorkflowEntity", params);
      setWorkflowEntityDialogOpen(false);

      if (result) {
        const hasSuccess =
          "successCount" in result ? result.successCount > 0 : result.success;
        if (hasSuccess) {
          selection.deselectAll();
        }
      }
    },
    [executeAction, selection]
  );

  return {
    executeAction,
    isExecuting,
    testSetDialogOpen,
    setTestSetDialogOpen,
    workflowEntityDialogOpen,
    setWorkflowEntityDialogOpen,
    handleActionClick,
    handleTestSetConfirm,
    handleWorkflowEntityConfirm,
  };
}

/**
 * Generate success message based on action and response
 */
function getSuccessMessage(
  action: EmailActionType,
  count: number,
  response: SingleEmailActionResponse | BatchEmailActionResponse
): string {
  if ("totalCount" in response) {
    // Batch response
    const batchRes = response as BatchEmailActionResponse;
    const base = `${batchRes.successCount}/${batchRes.totalCount} emails`;

    switch (action) {
      case "storeEmailInS3":
        return `${base} stored in S3`;
      case "addToTestSet":
        if (batchRes.actionData?.addedCount !== undefined) {
          return `${batchRes.actionData.addedCount} emails added to test set (${
            batchRes.actionData.alreadyInSetCount || 0
          } already in set)`;
        }
        return `${base} added to test set`;
      case "createWorkflowEntity":
        if (batchRes.actionData?.createdCount !== undefined) {
          return `${batchRes.actionData.createdCount} workflow entities created (${
            batchRes.actionData.alreadyExistCount || 0
          } already exist)`;
        }
        return `${base} workflow entities created`;
      default:
        return `${base} processed`;
    }
  } else {
    // Single response
    switch (action) {
      case "storeEmailInS3":
        if (response.data?.alreadyStored) {
          return "Email already stored in S3";
        }
        return "Email stored in S3";
      case "addToTestSet":
        if (response.data?.alreadyInSet) {
          return "Email already in test set";
        }
        return "Email added to test set";
      case "copyTestFixture":
        return "Test fixture generated";
      case "createWorkflowEntity":
        if (response.data?.alreadyInSet) {
          return "Workflow entity already exists";
        }
        return "Workflow entity created";
      default:
        return "Action completed";
    }
  }
}
